/**
 * transactions (transaction-records task) — group lifecycle records into
 * transactions and derive their state / headline / variance / qty×price
 * breakdown. Pure, client-side, no React/network (re-runs on every render like
 * computeBalanceSeries).
 *
 * This REPLACES the #1414 runtime matcher (reconcile.ts): the plan↔Ist match is
 * now the authored ``transaction_id``, not a heuristic amount×date score. The
 * ✓⏳⚠＋ states are re-derived here from which records a transaction carries plus
 * planned↔payed agreement (the tolerance is salvaged from reconcile.ts).
 *
 * The compute engine (computeBalanceSeries) is intentionally NOT changed: the
 * "payed-else-planned" series rule is achieved by feeding it payed records as
 * history (bank facts) and planned records as the projection — see
 * ``recordToBankFact`` / ``recordToPlanned``.
 */
import { stripCf } from './cashFlowProjection';
import { isLedgerCategory, UNSORTED } from './ledgerCategory';
import { isPlanWriteOfRecordSource } from './types';
import type {
  BankTransaction,
  Direction,
  LiquiditaetRecord,
  PlannedTransaction,
  ReconInfo,
  RecordRole,
  ReconState,
  Transaction,
} from './types';

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Signed money of a record: income → +total, expense → −total. */
export function signedTotal(r: { total_eur: number; direction: Direction }): number {
  return r.direction === 'income' ? r.total_eur : -r.total_eur;
}

// ── State derivation (tolerance salvaged from reconcile.ts) ────────────
/** ✓-vs-⚠ threshold, mirroring DEFAULT_RECONCILE_CONFIG (tightAbs 10, tightFrac 0.01). */
const TIGHT_ABS = 10;
const TIGHT_FRAC = 0.01;
const tightTol = (planAbs: number): number => Math.max(TIGHT_ABS, TIGHT_FRAC * planAbs);

const byDateAsc = (a: { value_date: string }, b: { value_date: string }): number =>
  a.value_date < b.value_date ? -1 : a.value_date > b.value_date ? 1 : 0;

/**
 * Fold records into transactions, keyed by ``transaction_id``. Canonical
 * attributes come from the planned record, else the earliest record by
 * ``value_date``. A second record of the same role on one transaction is ignored
 * (first wins) — authored data carries at most one per role.
 */
export function groupRecords(records: LiquiditaetRecord[]): Transaction[] {
  const byTx = new Map<string, LiquiditaetRecord[]>();
  for (const r of records) {
    const arr = byTx.get(r.transaction_id);
    if (arr) arr.push(r);
    else byTx.set(r.transaction_id, [r]);
  }
  const out: Transaction[] = [];
  for (const [id, recs] of byTx) {
    const pick = (role: RecordRole) => recs.find((r) => r.record_role === role);
    const planned = pick('planned');
    const contracted = pick('contracted');
    const payed = pick('payed');
    const canonical = planned ?? [...recs].sort(byDateAsc)[0];
    out.push({
      id,
      label: canonical.label,
      ledger_category: canonical.ledger_category,
      direction: canonical.direction,
      planned,
      contracted,
      payed,
    });
  }
  return out;
}

/**
 * The record that carries the transaction's headline (date + amount): payed,
 * else **planned**, else contracted.
 *
 * Planned outranks contracted (#1495): the cashflow series + running balance
 * are computed only from planned/payed records (contracted never reaches
 * ``computeBalanceSeries``). So an unpaid planned+contracted transaction must
 * headline at the PLANNED date/amount the series actually projected — otherwise
 * the row (e.g. contracted €1,200 on 07-20) diverges from the curve (which only
 * stepped €1,000 on 07-15). The contracted stage is not lost: ``LedgerFeed``
 * renders the Kontrahiert chip + ``doc_ref`` from ``tx.contracted`` and
 * ``transactionVariance.contractedVsPlannedEur`` surfaces the contracted figure.
 */
export function headRecord(tx: Transaction): LiquiditaetRecord {
  // A grouped transaction always has ≥1 record, so one of these is defined.
  return (tx.payed ?? tx.planned ?? tx.contracted) as LiquiditaetRecord;
}

/**
 * The record a payment is judged against: the plan if the farmer made one, else
 * the CONTRACT (#3026).
 *
 * ``planned`` first keeps #1495 intact — the state must agree with the curve, and
 * the curve is computed from planned/payed only. But a great many real payments
 * were never hand-planned and are committed by a document instead: the wibill
 * invoice expectations (``cash_flow status=contracted``) that a buyer credit
 * settles. Judging those against ``planned`` alone means judging them against
 * nothing, which is why every reconciled buyer payment read ＋ungeplant.
 *
 * ONE definition, four consumers — ``transactionState``'s verdict axis,
 * ``buildTransactionLedgerRows``' ``recon.commitment``/``deltaEur``, and
 * ``LedgerFeed``'s chip noun + detail panel — so the chip, the Δ and the panel
 * can never disagree about which figure the verdict was reached against.
 *
 * **The one exception to "planned first" (#3173): a plan that is a COPY of the
 * contract.** #3152's grouping create seeds the new plan straight from the
 * expectation's own headline (``amount_eur: Math.abs(row.amountEur)`` in
 * ``LiquiditaetsplanungPage``), so once a settling credit carries ``planned_ref``
 * the very rows the #3026 floor was written for grow a third record whose amount
 * is the contract's. Such a plan is not farmer-authored economics and adds no
 * information about the money; letting it demote the floor to two-sided equality
 * re-asserts "⚠ abweichend · Δ +500,00 €" on a fully reconciled collective
 * credit — the defect the floor exists to prevent. So when a payment exists and
 * the plan restates the contract, the CONTRACT judges. See
 * ``seededFromContract`` for why the equality is strict and direction-aware.
 */
export function commitmentRecord(tx: Transaction): LiquiditaetRecord | undefined {
  const kind = commitmentKind(tx);
  if (kind === 'planned') return tx.planned;
  if (kind === 'contracted') return tx.contracted;
  return undefined;
}

/**
 * Whether ``planned`` merely restates ``contracted`` (#3173) — the #3152-grouped
 * shape, as opposed to a plan the farmer authored with its own economics.
 *
 * - **Strict amount equality, deliberately.** The grouping create copies the
 *   expectation's amount verbatim, so an exact match is what "seeded" looks like
 *   and anything else is a farmer edit (``buildPlannedInput`` re-parses de-DE
 *   input). The cliff is the point: a plan hand-edited to 964,00 against a 963,00
 *   contract is authored, and judging it by the floor would swallow a real 1 €
 *   deviation. Do NOT widen this to ``tightTol`` — that tolerance answers "did
 *   the money agree", not "did the farmer author this plan".
 * - **Same direction required.** The floor compares MAGNITUDES, so an
 *   Einnahme plan of 963,00 against an Ausgabe contract of 963,00 would
 *   otherwise read as "equal" and let the floor bless a sign contradiction.
 *   #3172 pins the direction in the grouping modal, which stops new rows from
 *   drifting; this predicate is what keeps already-drifted rows out of the floor.
 */
function seededFromContract(planned: LiquiditaetRecord, contracted: LiquiditaetRecord): boolean {
  return planned.direction === contracted.direction && planned.total_eur === contracted.total_eur;
}

/** Which commitment ``commitmentRecord`` picked (``undefined`` = uncommitted). */
export function commitmentKind(tx: Transaction): 'planned' | 'contracted' | undefined {
  const { planned, contracted, payed } = tx;
  // #3173. Gated on ``payed``: with nothing booked there is no verdict to reach,
  // and an unpaid row must keep naming the PLAN its headline and the balance
  // series were computed from (#1495).
  if (payed && planned && contracted && seededFromContract(planned, contracted)) return 'contracted';
  if (planned) return 'planned';
  if (contracted) return 'contracted';
  return undefined;
}

/**
 * ReconState from record presence + payed↔commitment agreement.
 *
 * The two axes are deliberately NOT symmetric (#3026):
 *
 * - **planned ↔ payed is a two-sided equality.** A plan is authored for this one
 *   transaction, so a payment above OR below it deviates (unchanged from #1414).
 * - **contracted ↔ payed is a one-sided FLOOR** — only a *shortfall* deviates.
 *   The sole producer of a contracted+payed grouping is ``wibill_settlement_match``,
 *   which stamps ``planned_ref`` with ``matched_ids[0]``: ONE member of a possibly
 *   1:N collective (``worker/src/services/structured/wibill_backfill.py``, the
 *   ``WibillMatchResult`` docstring). Its own fixture is a €1.463,00 credit settling
 *   invoices of 963,00 + 500,00, stamping only the 963,00 one — so a credit
 *   EXCEEDING the stamped invoice is that invoice's unstamped siblings, not a
 *   discrepancy, and an equality rule would assert "⚠ abweichend · Δ +500,00 €" on a
 *   fully reconciled payment: a worse defect than the ＋ungeplant it replaced. A
 *   payment that falls SHORT is a real under-settlement (pass 1 is amount-blind, so
 *   instalments and Skonto deductions both land here) and is exactly what #3026
 *   wants surfaced, so it survives.
 *
 * The floor compares MAGNITUDES, so it reads the same for an income credit and an
 * expense debit: "at least the committed amount moved".
 *
 * WHICH axis applies is not decided here — it is read off ``commitmentKind``
 * (#3173), the single definition this function, ``buildTransactionLedgerRows``
 * and ``LedgerFeed`` all consume. That is what makes a three-record row
 * (planned + contracted + payed, from #3152's grouping create) impossible to
 * judge by one figure while the Δ and the panel name another: a plan that merely
 * restates the contract yields ``'contracted'`` and takes the floor; a plan with
 * its own amount yields ``'planned'`` and keeps two-sided equality.
 */
export function transactionState(tx: Transaction): ReconState {
  const { payed } = tx;
  const commitment = commitmentRecord(tx);
  if (payed && commitment) {
    if (commitmentKind(tx) === 'planned') {
      const delta = Math.abs(round2(signedTotal(payed) - signedTotal(commitment)));
      return delta <= tightTol(Math.abs(signedTotal(commitment))) ? 'bestaetigt' : 'abweichend';
    }
    const owed = Math.abs(signedTotal(commitment));
    const settled = Math.abs(signedTotal(payed));
    return settled >= round2(owed - tightTol(owed)) ? 'bestaetigt' : 'abweichend';
  }
  // #3026: ＋ungeplant now means what it says — booked against NO commitment of
  // either kind (an uncategorised bank line), not merely "unplanned".
  if (payed) return 'ungeplant';
  return 'geplant'; // planned-only or contracted-only (committed, not yet paid)
}

/** What a click on a categorize entry point should do (#3065). */
export type CategorizeAction = 'edit' | 'categorize' | 'none';

/** A row from either categorize entry point — just the fields the gate reads.
 *  Duck-typed rather than importing ``LedgerRow`` (defined in ``buildLedgerRows.ts``,
 *  which imports THIS module) to avoid a circular import — which is also why
 *  ``transaction`` is declared STRUCTURALLY here (the one key of the one record the
 *  gate reads) rather than as ``Transaction``. ``LedgerRow`` satisfies it by shape. */
export interface CategorizeGateRow {
  planned?: PlannedTransaction;
  recon?: ReconInfo;
  transaction?: { contracted?: { settled_at?: string } };
}

/**
 * ONE predicate for both categorize entry points (#3065) — ``LedgerFeed``'s
 * ``canCategorize`` and ``LiquiditaetsplanungPage``'s ``handleCategoryTxClick``
 * had drifted apart after #3026 widened which rows read ``bestaetigt``/
 * ``abweichend`` (a ``contracted``+``payed`` grouping now does too, not just
 * ``planned``+``payed``), so a settled buyer credit reached ``handleCategorize``
 * from the Kategorien view even though the ledger correctly refused it.
 *
 * Four shapes, in order:
 *  - has ``planned`` → ``'edit'`` (open the edit form, prefilled from the plan).
 *  - ``payed``-only, no commitment (``recon.state === 'ungeplant'``) →
 *    ``'categorize'`` (open the create form prefilled from the booked line —
 *    #1612). BY DEFINITION has no ``planned``, so the check above never shadows it.
 *  - ``contracted``-only, unpaid, and NOT settled (``recon.state === 'geplant'``,
 *    no ``planned``) → ``'categorize'`` — a real feature: the farmer can plan
 *    around a still-open Rechnung. NOT part of the #3065 bug.
 *  - ``contracted``-only but SETTLED → ``'none'`` (#3140). See below — this is a
 *    money guard, not a nicety.
 *  - ``contracted``+``payed`` (``bestaetigt``/``abweichend``, no ``planned``) →
 *    ``'none'`` — the invoice's own document already categorised it; before this
 *    fix the Kategorien view wrongly re-opened the categorize modal here.
 *
 * **The ``settled_at`` narrowing is a MONEY guard (#3140).** A user plan does not
 * stay in the display layer: ``engineRecordsFrom`` (``cashFlowProjection.ts``)
 * overlays every ``planned`` + ``source: 'user'`` record onto ``computeBalanceSeries``.
 * An UNPAID expectation's money is in the curve ZERO times, so planning it puts it
 * in once — the intended feature. A SETTLED expectation (expectations 2..N of a 1:N
 * collective credit, stamped by ``wibill_settlement_match``) is money that ALREADY
 * ARRIVED and is in the curve once via the credit's ``payed`` side; planning it puts
 * it in a SECOND time. ``zeroBalanceEffect`` does NOT save this — it zeroes the
 * expectation's OWN row, while the farmer's plan is a different transaction with a
 * live amount. Offering the affordance unguarded would breach #3107's own success
 * criterion, "no double count of MONEY".
 *
 * The guard lives HERE rather than only in ``LedgerFeed``'s ``canCategorize`` so the
 * two entry points cannot drift apart again the way they did after #3026 — the
 * Kategorien path is inert on this shape only because of ``categoryRollup``'s
 * separate phantom-booking guard, which is not this predicate's to rely on.
 */
export function categorizeAction(row: CategorizeGateRow): CategorizeAction {
  if (row.planned) return 'edit';
  const state = row.recon?.state;
  // Only the contracted-only shape reaches 'geplant' without a plan; a settled one
  // is money already in the curve. Read verbatim and truthy-gated — an empty string
  // is not a settlement date.
  if (state === 'geplant' && row.transaction?.contracted?.settled_at) return 'none';
  if (state === 'ungeplant' || state === 'geplant') return 'categorize';
  return 'none';
}

/** Signed headline money: payed, else planned, else contracted (see ``headRecord``). */
export function transactionHeadlineSigned(tx: Transaction): number {
  return signedTotal(headRecord(tx));
}

export interface TransactionVariance {
  /** payed − planned (signed); null unless both present. */
  payedVsPlannedEur: number | null;
  /** contracted − planned (signed); null unless both present. */
  contractedVsPlannedEur: number | null;
  /** payed − contracted (signed); null unless both present. The Δ of a
   *  plan-less settled invoice (#3026) — how much of the Rechnung the buyer
   *  actually paid. Without it the ⚠ abweichend chip on such a row has no Δ. */
  payedVsContractedEur: number | null;
}

export function transactionVariance(tx: Transaction): TransactionVariance {
  const p = tx.planned ? signedTotal(tx.planned) : null;
  const c = tx.contracted ? signedTotal(tx.contracted) : null;
  return {
    payedVsPlannedEur: tx.payed && p != null ? round2(signedTotal(tx.payed) - p) : null,
    contractedVsPlannedEur: c != null && p != null ? round2(c - p) : null,
    payedVsContractedEur: tx.payed && c != null ? round2(signedTotal(tx.payed) - c) : null,
  };
}

export interface QtyPriceBreakdown {
  qty: number;
  unitPriceEur: number;
  totalEur: number;
}

/** The qty×price breakdown when the line has a real quantity (qty ≠ 1), else null. */
export function qtyPriceBreakdown(r: LiquiditaetRecord): QtyPriceBreakdown | null {
  if (r.qty === 1) return null;
  return { qty: r.qty, unitPriceEur: r.unit_price_eur, totalEur: r.total_eur };
}

// ── Record → legacy-shape converters (feed the UNCHANGED engine) ───────

/** The id namespace the planned-transaction mutation route accepts (backend
 *  ``_PLAN_ID``). Native records are seeded under ``liq-rec-*`` and the route
 *  rejects those, so an edit must post a ``liq-plan-*`` id instead. */
const PLAN_PREFIX = 'liq-plan-';

/** Strip the ``liq-plan-`` namespace prefix to recover the underlying id.
 *  ANCHORED prefix op only (colon-bearing entity ids permeate the twin — never
 *  split on ``:`` or ``-``); a non-prefixed id passes through unchanged.
 *  Exported since #3171: ``plannedToRecord`` (fact-path ledger fold) and
 *  ``planSupersededByPayed`` (engine-feed drop) must strip identically — two
 *  copies of this rule are exactly how the join silently breaks (``stripCf``). */
export function stripPlanPrefix(id: string): string {
  return id.startsWith(PLAN_PREFIX) ? id.slice(PLAN_PREFIX.length) : id;
}

/**
 * The #1653 payed-supersedes-plan drop predicate, scheme-tolerant since #3171.
 *
 * A planned record is superseded when its ``transaction_id``
 *  - exactly matches a payed record's id (the pre-#3171 rule — the migration
 *    pairing depends on it: the migration writes ``planned_ref =
 *    cf-liq-plan-…``, so THAT payed sibling reconstructs the FULL
 *    ``liq-plan-…`` id), OR
 *  - lives in the ``liq-plan-`` namespace and its strip-normalised id matches
 *    (ADDITIVE, #3171): the engine feeds re-key a user plan to its FULL
 *    ``derived_fact_id`` (``liq-plan-wibill-inv:<n>``) while a settling
 *    credit's payed record keys the bare entity scheme (``wibill-inv:<n>``,
 *    from ``stripCf(planned_ref)``) — without the strip tolerance a
 *    #3152-grouped, future-re-dated plan double-counts once its expectation
 *    settles.
 *
 * Guards: the strip is anchored; an EMPTY id after the strip never matches
 * (#2611 pattern). Collision safety: an ordinary create's ``liq-plan-user-
 * <uuid>`` strips to ``user-<uuid>``, a shape no payed scheme mints.
 */
export function planSupersededByPayed(
  transactionId: string,
  payedTxIds: ReadonlySet<string>,
): boolean {
  if (payedTxIds.has(transactionId)) return true;
  if (!transactionId.startsWith(PLAN_PREFIX)) return false;
  const stripped = stripPlanPrefix(transactionId);
  return stripped !== '' && payedTxIds.has(stripped);
}

/**
 * The id the edit form posts for a planned record. A user-created planned fact
 * already lives in the ``liq-plan-*`` namespace → keep its id. A native
 * ``liquiditaet_record`` (``liq-rec-*``) maps to a DETERMINISTIC
 * ``liq-plan-<transaction_id>`` so (a) the mutation route accepts it and
 * (b) the legacy fact it writes supersedes this record — ``plannedToRecord``
 * strips the prefix back to the same ``transaction_id`` (see ``mergeRecords``).
 */
export function planEditId(r: { transaction_id: string; derived_fact_id?: string }): string {
  if (r.derived_fact_id?.startsWith(PLAN_PREFIX)) return r.derived_fact_id;
  return `${PLAN_PREFIX}${r.transaction_id}`;
}

/** A planned record → the ``PlannedTransaction`` shape computeBalanceSeries expects (projection feed). */
export function recordToPlanned(r: LiquiditaetRecord): PlannedTransaction {
  return {
    kind: 'planned',
    ledger_category: r.ledger_category,
    label: r.label,
    direction: r.direction,
    amount_eur: r.total_eur,
    qty: r.qty,
    unit_price_eur: r.unit_price_eur,
    value_date: r.value_date,
    period_ym: r.period_ym || r.value_date.slice(0, 7),
    // HOF-135 §3 — read the widened vocabulary THROUGH the pivot. This is the hop
    // that un-collapses codex Angle-B finding 5: ``toPlanned`` read ``bwa`` off the
    // fact, ``plannedToRecord`` carried it, and this line threw it away.
    source: isPlanWriteOfRecordSource(r.source) ? r.source : 'ws26_27_excel',
    // Edit-safe id (route accepts liq-plan-*; native liq-rec-* would 422).
    derived_fact_id: planEditId(r),
    notes: r.notes,
    // #2211: carry the record's owning Konto so ``buildLedgerRows`` → ``row.planned``
    // → the edit modal pre-selects it (reassign starts at the current account).
    // Conditional key keeps account-less rows account-less (byte-identity).
    ...(r.account_id ? { account_id: r.account_id } : {}),
    // #2678 c: same hop for the Gegenpartei link — ``row.planned`` is what the edit
    // modal seeds from, so dropping it here would show an empty picker on a linked
    // row and let the next save unlink it (wholesale fact_value overwrite).
    ...(r.counterparty_id ? { counterparty_id: r.counterparty_id } : {}),
    // HOF-135 §3 — the provenance rides back out of the pivot. ``row.planned`` is
    // what the PlugChip reads, so a key dropped here renders the plug as an ordinary
    // Investition in the LIVE page while the component test (which builds rows by
    // hand) stays green — exactly codex Angle-B finding 1.
    ...(r.method ? { method: r.method } : {}),
    ...(r.factor != null ? { factor: r.factor } : {}),
    ...(r.lag_days != null ? { lag_days: r.lag_days } : {}),
    ...(r.is_plug ? { is_plug: true } : {}),
    ...(r.assumption ? { assumption: r.assumption } : {}),
    ...(r.base_ref ? { base_ref: r.base_ref } : {}),
  };
}

/** A payed record → the ``BankTransaction`` shape (history feed). ``amount_eur`` is signed. */
export function recordToBankFact(r: LiquiditaetRecord): BankTransaction {
  return {
    kind: 'fact',
    booking_date: r.value_date,
    amount_eur: signedTotal(r),
    counterparty: r.label,
    purpose: r.label,
    statement_id: r.statement_id ?? '',
    period_ym: r.period_ym || r.value_date.slice(0, 7),
  };
}

// ── Legacy fact compat (typed planned/bank objects → records) ──────────

const slug = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'x';

/** A legacy planned transaction → a planned record. Its ``transaction_id`` is the
 *  edit id with the ``liq-plan-`` prefix stripped, so a user edit of a native
 *  record (posted as ``liq-plan-<native transaction_id>``) folds back onto that
 *  same ``transaction_id`` and supersedes it (see ``mergeRecords``); a fresh
 *  create (``liq-plan-user-<uuid>``) yields a unique id with nothing to supersede. */
export function plannedToRecord(p: PlannedTransaction): LiquiditaetRecord {
  const total = p.amount_eur;
  // Carry the manually-entered volume × price when present; an amount-only
  // line (no qty) reads as qty 1 × the total.
  const qty = p.qty != null && p.qty > 0 ? p.qty : 1;
  const unitPrice = p.unit_price_eur != null ? p.unit_price_eur : total;
  const txId = p.derived_fact_id
    ? stripPlanPrefix(p.derived_fact_id)
    : `tx-plan-${p.value_date}-${slug(p.label)}`;
  return {
    transaction_id: txId,
    record_role: 'planned',
    qty,
    unit_price_eur: unitPrice,
    total_eur: total,
    value_date: p.value_date,
    ledger_category: p.ledger_category,
    direction: p.direction,
    label: p.label,
    period_ym: p.period_ym || p.value_date.slice(0, 7),
    source: isPlanWriteOfRecordSource(p.source) ? p.source : 'excel',
    notes: p.notes,
    derived_fact_id: p.derived_fact_id,
    // #2211: carry the account through so mergeRecords' superseding user edit keeps
    // it in the feed record set — this is what re-homes a reassigned plan between
    // per-account feeds. Conditional key: an account-less plan stays account-less.
    ...(p.account_id ? { account_id: p.account_id } : {}),
    // #2678 c: likewise the Gegenpartei link, so the superseding user edit keeps it
    // in the record set instead of dropping it on the way back out.
    ...(p.counterparty_id ? { counterparty_id: p.counterparty_id } : {}),
    // HOF-135 §3 — provenance INTO the pivot (mirror of ``recordToPlanned``'s way out).
    ...(p.method ? { method: p.method } : {}),
    ...(p.factor != null ? { factor: p.factor } : {}),
    ...(p.lag_days != null ? { lag_days: p.lag_days } : {}),
    ...(p.is_plug ? { is_plug: true } : {}),
    ...(p.assumption ? { assumption: p.assumption } : {}),
    ...(p.base_ref ? { base_ref: p.base_ref } : {}),
  };
}

/**
 * The id namespaces owned by the legacy FACT families. `cash_flow.planned_ref` is
 * a generic ontology field ("the planned cash_flow this booking fulfils") with
 * TWO producers, and only one of them is a settlement:
 *
 *  - `wibill_settlement_match` writes an ENTITY id (`cf-wibill-inv:<n>`) — the
 *    expectation this credit settles. That is what #3137 re-keys on.
 *  - `liquiditaet_projection` (the migration) writes `cf-<planned record's
 *    derived_fact_id>` = `cf-liq-plan-…` for every contracted/payed row.
 *
 * `stripCf` alone turns the second into `liq-plan-user-xyz`, but the fact path's
 * `plannedToRecord` strips `liq-plan-` as well and yields `user-xyz` — so the two
 * never meet and re-keying on it would invent an id nothing else uses, splitting
 * a paired transaction. Refuse those refs outright: only an ENTITY-namespace ref
 * earns a re-key.
 */
const LEGACY_RECORD_PREFIXES = ['liq-plan-', 'liq-rec-', 'tx-bank-'] as const;

/** The expectation id a bank line settles, or `''` when it settles nothing we can
 *  safely key on (absent, empty after the `cf-` strip, or a legacy-fact id). */
function settlementKeyOf(b: BankTransaction): string {
  if (!b.planned_ref) return '';
  const key = stripCf(b.planned_ref);
  if (!key) return '';
  return LEGACY_RECORD_PREFIXES.some((p) => key.startsWith(p)) ? '' : key;
}

/**
 * A legacy bank line → a payed record.
 *
 * The ``transaction_id`` is the line's own DISTINCT synthetic id (no authored
 * match → it surfaces as ＋ungeplant, never falsely paired) — **unless the fact
 * carries a ``planned_ref``** (#3137), in which case the credit is keyed on the
 * expectation it SETTLES and the two fold into one row.
 *
 * Nothing is matched here. ``wibill_settlement_match`` already resolved which
 * expectation this credit settles and stamped ``planned_ref`` on the ``cash_flow``;
 * the worker materialiser now carries it onto the fact, and ``cashFlowToRecord``
 * derives the expectation's own ``transaction_id`` from the identical
 * ``stripCf(planned_ref)`` rule — which is why ``stripCf`` is imported rather than
 * re-implemented.
 *
 * ``derived_fact_id`` deliberately stays the SYNTHETIC id whichever key wins:
 * ``contractedRecordsFrom``'s provenance dedupe and the backend migration's
 * ``_source_id_of`` (which content-hashes anything starting ``tx-bank-``) both
 * read it. Only ``transaction_id`` moves.
 *
 * Prefer ``bankRecordsFrom`` for a whole fact set — it additionally refuses the
 * re-key when two credits share one ``planned_ref`` (see there).
 */
export function bankToRecord(b: BankTransaction, index = 0): LiquiditaetRecord {
  const total = Math.abs(b.amount_eur);
  const direction: Direction = b.amount_eur >= 0 ? 'income' : 'expense';
  const id = `tx-bank-${b.statement_id}-${b.booking_date}-${index}`;
  return {
    transaction_id: settlementKeyOf(b) || id,
    record_role: 'payed',
    qty: 1,
    unit_price_eur: total,
    total_eur: total,
    value_date: b.booking_date,
    // #2161: honour a fact-carried category iff it is a vocabulary id; a
    // manual liq-plan-* planned sibling still wins via groupRecords'
    // planned-first canonical rule.
    ledger_category: isLedgerCategory(b.ledger_category) ? b.ledger_category : UNSORTED,
    direction,
    label: b.counterparty || b.purpose || 'Buchung',
    period_ym: b.period_ym || b.booking_date.slice(0, 7),
    statement_id: b.statement_id,
    derived_fact_id: id,
    // #2211: carry the fact-threaded account so the feed can scope by Konto
    // (conditional key — an account-less line stays account-less → overlay bucket).
    ...(b.account_id ? { account_id: b.account_id } : {}),
    // #3160 (FE mirror of #3142's bank_to_record): the second half of the same
    // chain — this function ALSO rebuilds its dict key-by-key, so a key toBank
    // passes through but this one drops is still invisible to every consumer.
    // Conditional, so every existing bank record stays byte-identical; the
    // source's condition INCLUDES the id's, so a source can never land without
    // its link (a provenance for a link that is not there describes nothing).
    ...(b.counterparty_id ? { counterparty_id: b.counterparty_id } : {}),
    ...(b.counterparty_id && b.counterparty_id_source
      ? { counterparty_id_source: b.counterparty_id_source }
      : {}),
    // #3187: the Notiz, second half of the same two-function chain — this function
    // ALSO rebuilds its object key-by-key, so a key toBank passes but this one drops
    // is still invisible. LedgerFeed reads LiquiditaetRecord.notes, so THIS hop is
    // what actually puts the farmer's text on the screen. Conditional, so every
    // existing bank record stays byte-identical.
    ...(b.notes ? { notes: b.notes } : {}),
  };
}

/**
 * A whole bank fact set → payed records, with the #3137 re-key applied SAFELY.
 *
 * ``groupRecords`` keeps only the FIRST record of each role on a transaction, so
 * if two credits carried the same ``planned_ref`` the second would be silently
 * dropped — a real bank credit vanishing from the ledger and from the running
 * balance. That is strictly worse than the cosmetic two-row defect #3137 fixes,
 * so a colliding ``planned_ref`` is refused for EVERY member of the collision and
 * all of them fall back to their synthetic ids (exactly today's behaviour).
 *
 * ``wibill_settlement_match`` cannot produce a collision within one run
 * (``matched_expectations`` prevents reuse) and the transform is deterministic —
 * but ``planned_ref`` is an operator-editable attribute persisted as a declared
 * PATCH (``owned_attribute_keys``), so the invariant is not enforced by the store
 * and is cheap to defend here.
 *
 * The un-settled path is byte-identical to the indexed ``map`` it replaces: the
 * index is the array position either way.
 */
export function bankRecordsFrom(
  banks: BankTransaction[],
  claimedIds: ReadonlySet<string> = new Set<string>(),
): LiquiditaetRecord[] {
  const seen = new Map<string, number>();
  for (const b of banks) {
    const key = settlementKeyOf(b);
    if (key) seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  return banks.map((b, i) => {
    const key = settlementKeyOf(b);
    const synthetic = `tx-bank-${b.statement_id}-${b.booking_date}-${i}`;
    // Two independent refusals, both degrading to today's exact rendering:
    //  - a COLLISION (two credits, one ref) — groupRecords is first-wins per
    //    role, so merging both would delete a real credit from the ledger;
    //  - an already-CLAIMED synthetic id — the credit is one half of an existing
    //    pair (the #1612 categorize flow keys a plan on `liq-plan-<synthetic>`),
    //    and re-keying away would strand that plan as a phantom row AND break
    //    #1653's payed-supersedes-its-plan drop, double-counting the money.
    const refused = !key || (seen.get(key) ?? 0) > 1 || claimedIds.has(synthetic);
    if (!refused) return bankToRecord(b, i);
    const { planned_ref: _refused, ...withoutRef } = b;
    return bankToRecord(withoutRef, i);
  });
}

/**
 * Assemble the unified record set the ledger + engine read from.
 *
 * **No native records** (a pre-migration tenant): fold all legacy planned/bank
 * facts as-is — they are the only data.
 *
 * **Native records present** (the seeded pilot): the native records are
 * authoritative. Only deliberate user edits/creates — legacy planned facts the
 * mutation route stamps ``source: 'user'`` — are layered on; one SUPERSEDES the
 * native planned record sharing its ``transaction_id`` (so an edit moves the row
 * instead of double-counting). Stale non-user legacy planned facts and ALL
 * legacy bank facts are dropped, so a booked cashflow can't be counted twice
 * (native payed + a legacy bank line re-added as a fresh ``ungeplant`` tx) or be
 * overridden by an older row.
 *
 * **``entityContracted`` (#3107) — which set wins.** The fourth arm carries the
 * ``cash_flow``-sourced CONTRACTED records (``contractedRecordsFrom``). It is
 * purely ADDITIVE and always loses to the fact arms, by construction rather than
 * by a rule here: the caller has already dropped every entity record whose
 * ``derived_fact_id`` appears among the fact records this function returns, so
 * anything still in this arm has no fact twin to conflict with. It is therefore
 * appended, never superseding and never superseded.
 *
 * Two consequences worth stating, because both are load-bearing:
 *  - the dedupe MUST run against the records that SURVIVE this fold, not
 *    against its raw inputs — a fact record this function drops must not keep
 *    suppressing its entity counterpart, or the row vanishes from the ledger
 *    entirely;
 *  - the fact family stays the write-of-record for everything it already
 *    covers (#2004 D3 keeps ``liquiditaet_*`` frozen), so this arm may only
 *    ever ADD the contracted role an entity-substrate tenant has no facts for.
 */
export function mergeRecords(
  native: LiquiditaetRecord[],
  legacyPlanned: LiquiditaetRecord[],
  legacyBank: LiquiditaetRecord[],
  entityContracted: LiquiditaetRecord[] = [],
): LiquiditaetRecord[] {
  const facts = mergeFactRecords(native, legacyPlanned, legacyBank);
  return entityContracted.length === 0 ? facts : [...facts, ...entityContracted];
}

function mergeFactRecords(
  native: LiquiditaetRecord[],
  legacyPlanned: LiquiditaetRecord[],
  legacyBank: LiquiditaetRecord[],
): LiquiditaetRecord[] {
  if (native.length === 0) return [...legacyPlanned, ...legacyBank];
  // HOF-135 §3 — the filter's job is "drop the EXCEL SEED once native records exist",
  // not "drop everything non-user". Widened in lockstep with the Python
  // ``merge_records`` and the write-through fold, so a generated (bwa/model) plan is
  // a live write-of-record on a native tenant instead of invisible money (codex
  // round 2, P1). The D2 overlay (``userPlansFrom``) stays user-only — a generated
  // row reaches the CURVE through its write-through cash_flow, never the overlay.
  const livePlanned = legacyPlanned.filter((r) => isPlanWriteOfRecordSource(r.source));
  const superseded = new Set(livePlanned.map((r) => r.transaction_id));
  const keptNative = native.filter(
    (r) => !(r.record_role === 'planned' && superseded.has(r.transaction_id)),
  );
  return [...keptNative, ...livePlanned];
}
